import { useEffect, useRef, useState } from 'react';
import { LOGO_MAX_BYTES, LOGO_TIPOS } from '@copathy/shared';
import { apiBlob } from '../lib/api';

/**
 * Logotipo del cliente, servido detrás de autenticación.
 *
 * ── Por qué no un `<img src="/api/…">` a secas ──
 * El access token vive **en memoria**, no en una cookie, así que un `<img src>` no
 * lo manda: el navegador pediría la imagen sin cabecera `Authorization` y el
 * servidor respondería 401. Se descarga por `fetch` y se muestra como object URL.
 *
 * El object URL se libera al desmontar. Sin eso, cada vez que alguien recorre el
 * listado se acumulan blobs en memoria que el recolector no puede tocar, porque
 * el propio `URL.createObjectURL` los mantiene vivos.
 *
 * ⚠️ Los `blob:` los bloquea la CSP de `helmet` cuando Express sirve la SPA — no
 * en el dev server de Vite—. Está declarado en `img-src` en `src/app.ts`.
 */
export function LogoCliente({
  clienteId,
  nombre,
  tieneLogo,
  tamano = 'tarjeta',
}: {
  clienteId: number;
  nombre: string;
  tieneLogo: boolean;
  tamano?: 'tarjeta' | 'detalle';
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!tieneLogo) {
      setUrl(null);
      setFallo(false);
      return;
    }
    let activo = true;
    let creada: string | null = null;

    apiBlob(`/api/empresas/${clienteId}/logo`)
      .then((blob) => {
        if (!activo) return;
        creada = URL.createObjectURL(blob);
        setUrl(creada);
      })
      .catch(() => activo && setFallo(true));

    return () => {
      activo = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [clienteId, tieneLogo]);

  const medida = tamano === 'detalle' ? 'h-24 w-24 text-4xl' : 'h-16 w-16 text-2xl';
  const marco = `flex shrink-0 items-center justify-center overflow-hidden rounded-2xl ${medida}`;

  /*
    Sin logotipo —o si falló al traerlo— va la inicial. No es un logotipo, y no
    pretende serlo: es un ancla visual para poder distinguir tarjetas de un
    vistazo mientras el cliente no haya cargado el suyo.
  */
  if (!tieneLogo || fallo) {
    return (
      <span aria-hidden="true" className={`${marco} bg-marino-50 font-bold text-marino-600`}>
        {nombre.trim().charAt(0).toUpperCase()}
      </span>
    );
  }

  if (!url) return <span className={`${marco} animate-pulse bg-marino-50`} />;

  return (
    <span className={`${marco} border border-marino-100 bg-white`}>
      {/*
        `object-contain` y no `cover`: un logotipo recortado deja de ser el
        logotipo. Se mete dentro del cuadro completo, con el fondo blanco a los
        lados si su proporción no coincide.
      */}
      <img src={url} alt={`Logotipo de ${nombre}`} className="h-full w-full object-contain p-1.5" />
    </span>
  );
}

/**
 * Campo para elegir el logotipo dentro del formulario.
 *
 * No sube nada por su cuenta: entrega el `File` al formulario, que lo manda
 * **después** de que el cliente exista. Al registrar todavía no hay id contra el
 * que subir, y subir a un id inventado es el camino corto a archivos huérfanos.
 */
export function CampoLogo({
  archivo,
  urlActual,
  nombre,
  alElegir,
  alQuitar,
  error,
}: {
  archivo: File | null;
  /** `true` si el cliente ya tiene uno guardado. */
  urlActual: boolean;
  nombre: string;
  alElegir: (f: File | null) => void;
  alQuitar?: () => void;
  error?: string | undefined;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    if (!archivo) {
      setVistaPrevia(null);
      return;
    }
    const u = URL.createObjectURL(archivo);
    setVistaPrevia(u);
    return () => URL.revokeObjectURL(u);
  }, [archivo]);

  function elegir(f: File | null) {
    setAviso(null);
    if (!f) {
      alElegir(null);
      return;
    }
    /*
      Se valida ANTES de subir. El servidor vuelve a validar —es el que decide—,
      pero avisar acá evita mandar 8 MB por una conexión lenta para que rebote.
    */
    if (!(LOGO_TIPOS as readonly string[]).includes(f.type)) {
      setAviso('Tiene que ser PNG, JPG o WebP.');
      return;
    }
    if (f.size > LOGO_MAX_BYTES) {
      setAviso(`No puede pesar más de ${Math.round(LOGO_MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    alElegir(f);
  }

  const mensaje = error ?? aviso;

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-marino-400">
        Logotipo
      </p>
      <div className="flex items-center gap-4">
        <span className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-marino-100 bg-white">
          {vistaPrevia ? (
            <img src={vistaPrevia} alt="" className="h-full w-full object-contain p-1.5" />
          ) : urlActual ? (
            <span className="text-xs font-semibold text-marino-500">Cargado</span>
          ) : (
            <span aria-hidden="true" className="text-2xl font-bold text-marino-300">
              {nombre.trim().charAt(0).toUpperCase() || '—'}
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          <input
            ref={entrada}
            type="file"
            accept={LOGO_TIPOS.join(',')}
            className="sr-only"
            onChange={(e) => elegir(e.target.files?.[0] ?? null)}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => entrada.current?.click()}
              className="rounded-2xl border border-marino-200 bg-white px-3.5 py-2 text-sm font-semibold
                text-marino-700 transition hover:bg-marino-50"
            >
              {archivo || urlActual ? 'Cambiar' : 'Elegir imagen'}
            </button>
            {(archivo || urlActual) && (
              <button
                type="button"
                onClick={() => {
                  alElegir(null);
                  if (entrada.current) entrada.current.value = '';
                  if (urlActual) alQuitar?.();
                }}
                className="rounded-2xl px-3.5 py-2 text-sm font-semibold text-marino-500 transition
                  hover:text-red-600 hover:underline"
              >
                Quitar
              </button>
            )}
          </div>
          {/*
            Decía «se recorta», y es falso: el servidor usa `fit: 'inside'`, que
            mete la imagen dentro del cuadro conservando su proporción. Un logotipo
            recortado deja de ser el logotipo, y decirle a alguien que se le va a
            recortar lo hace subir una versión cuadrada que no hace falta.
          */}
          <p className="mt-1.5 text-xs text-marino-400">
            PNG, JPG o WebP, hasta {Math.round(LOGO_MAX_BYTES / 1024 / 1024)} MB. Se ajusta al
            cuadro completo, sin recortarlo ni deformarlo.
          </p>
        </div>
      </div>

      {mensaje && <p className="mt-2 text-xs font-medium text-red-600">{mensaje}</p>}
    </div>
  );
}
